<template>
  <EXTooltip title="Click to see your assigned responsibilities" placement="top">
    <div
      class="assigned-responsibilities-dashboard-card assigned-responsibilities-dashboard-card--clickable"
      role="link"
      tabindex="0"
      @click="onNavigate"
      @keydown.enter.prevent="onNavigate"
      @keydown.space.prevent="onNavigate"
    >
      <div class="assigned-responsibilities-dashboard-card__header">
        <div class="assigned-responsibilities-dashboard-card__icon-container">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9 17H7C4.23858 17 2 14.7614 2 12C2 9.23858 4.23858 7 7 7H9M8 12L18 12M15.7778 17H17C19.7614 17 22 14.7614 22 12C22 9.23858 19.7614 7 17 7H15.7778C15.3482 7 15 7.34822 15 7.77778V16.2222C15 16.6518 15.3482 17 15.7778 17Z" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="assigned-responsibilities-dashboard-card__text-container">
          <div class="assigned-responsibilities-dashboard-card__title">
            {{ cardTitle }} ({{ total_assignments }})
          </div>
          <div class="assigned-responsibilities-dashboard-card__subtitle">
            Count by Readiness Level
          </div>
        </div>
      </div>
      <div class="assigned-responsibilities-dashboard-card__body">
        <CountOfRespAssignmentsByLevel
          :count_of_apprentice="assigned_responsibilities_with_apprentice_level"
          :count_of_professional="assigned_responsibilities_with_professional_level"
          :count_of_coach="assigned_responsibilities_with_coach_level"
          :count_of_master="assigned_responsibilities_with_master_level"
          :count_of_novice="assigned_responsibilities_with_novice_level"
          :hovered="true"
        />
      </div>
    </div>
  </EXTooltip>
</template>

<script setup>
import { computed, getCurrentInstance } from "vue";
import { useRouter } from "vue-router/composables";
import CountOfRespAssignmentsByLevel from "@components/CountOfRespAssignmentsByLevel/CountOfRespAssignmentsByLevel.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";

const emit = defineEmits(["navigateToEmployeeResponsibilities"]);

const instance = getCurrentInstance();
const router = instance?.proxy?.$router || useRouter();

const props = defineProps({
  assigned_responsibilities_with_apprentice_level: {
    type: Number,
    default: 0,
  },
  assigned_responsibilities_with_professional_level: {
    type: Number,
    default: 0,
  },
  assigned_responsibilities_with_coach_level: {
    type: Number,
    default: 0,
  },
  assigned_responsibilities_with_master_level: {
    type: Number,
    default: 0,
  },
  assigned_responsibilities_with_novice_level: {
    type: Number,
    default: 0,
  },
  cardTitle: {
    type: String,
    default: "Your Responsibilities",
  },
});

const total_assignments = computed(() => {
  return (
    (props.assigned_responsibilities_with_apprentice_level || 0) +
    (props.assigned_responsibilities_with_professional_level || 0) +
    (props.assigned_responsibilities_with_coach_level || 0) +
    (props.assigned_responsibilities_with_master_level || 0) +
    (props.assigned_responsibilities_with_novice_level || 0)
  );
});

function onNavigate() {
  if (router && typeof router.push === "function") {
    router.push({ name: "EmployeeResponsibilities" }).catch(() => {});
    return;
  }
  emit("navigateToEmployeeResponsibilities");
}
</script>

<style scoped>
.assigned-responsibilities-dashboard-card {
  padding: 24px;
  border-radius: 12px;
  border: 1px solid #EAECF0;
  background: #FFF;
  box-shadow: 0px 1px 2px 0px #1018280D;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 330px;
  max-width: 330px;
  height: 162px;
}

.assigned-responsibilities-dashboard-card--clickable {
  cursor: pointer;
}

.assigned-responsibilities-dashboard-card--clickable:focus-visible {
  outline: 2px solid #667085;
  outline-offset: 2px;
}

.assigned-responsibilities-dashboard-card__header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.assigned-responsibilities-dashboard-card__icon-container {
  display: flex;
  width: 48px;
  height: 48px;
  padding: 12px;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  border: 1px solid #EAECF0;
  background: #FFF;
}

.assigned-responsibilities-dashboard-card__text-container {
  display: flex;
  flex-direction: column;
  position: relative;
  top: 1px;
}

.assigned-responsibilities-dashboard-card__title {
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #344054;
}

.assigned-responsibilities-dashboard-card__subtitle {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}

.assigned-responsibilities-dashboard-card__body {
  display: flex;
  justify-content: end;
  position: relative;
  left: 6px;
  top: -6px;
}
</style>
